import moment from 'moment';
import { delay, put, select, takeEvery } from 'redux-saga/effects';
import { generateToastr } from '../../core/toastMessages';
import { completeTaskAOT, snoozeTaskCompletion } from '../overview/actions';
import { fetchTasks, getLastTasksQuery } from './actions';
import { TaskType } from './models';
import { selectCurrentEmployee, selectTasks } from './reducer';

const completionIdFrom = (completion) =>
  completion?.id ??
  completion?._id ??
  completion?.taskCompletionId ??
  completion?.task_completion_id ??
  completion?.completionId ??
  completion?.uuid ??
  completion?.guid;

const isProjectTask = (task = {}) => {
  const type = task.type;
  return type === TaskType.Project || type === 'Project' || type === 1 || type === '1';
};

function* refreshTasks() {
  const query = getLastTasksQuery();
  yield put(fetchTasks(query));
}

function* waitForCompletion(taskId) {
  for (let attempt = 0; attempt < 8; attempt += 1) {
    yield delay(500);
    const tasks = yield select(selectTasks);
    const found = Array.isArray(tasks) && tasks.find ? tasks.find((t) => t.id === taskId) : null;
    const completionId = completionIdFrom(found?.latestCompletion);
    if (completionId) {
      return completionId;
    }
  }
  return null;
}

function normalizeStatus(status) {
  if (!status) return '';
  const lower = String(status).toLowerCase();
  if (lower.includes('progress')) return 'In Progress';
  if (lower.includes('complete')) return 'Completed';
  if (lower.includes('todo') || lower.includes('to do')) return 'To Do';
  return status;
}

function* handleSaveTaskSuccess() {
  generateToastr('success', 'Task saved', 'The task was saved successfully.');
}

function* handleAddTaskSuccess(action) {
  const desiredStatus = normalizeStatus(action?.meta?.desiredStatus);
  const task = action?.response;
  const isProject = task && isProjectTask(task);
  if (isProject && (!desiredStatus || desiredStatus === 'To Do')) {
    generateToastr('success', 'Work order created', 'Visible under the New status filter', {
      timeOut: 3000,
    });
    return;
  }
  if (!desiredStatus || desiredStatus === 'To Do') return;
  if (!isProject) return;

  const employeeId = task.employeeId || (yield select(selectCurrentEmployee));
  if (!employeeId) return;

  if (desiredStatus === 'Completed') {
    yield put(
      completeTaskAOT({
        taskId: task.id,
        employeeId,
        notes: null,
      })
    );
    yield delay(750);
    yield* refreshTasks();
    return;
  }

  if (desiredStatus === 'In Progress') {
    yield put(
      completeTaskAOT({
        taskId: task.id,
        employeeId,
        notes: null,
      })
    );
    const completionId = yield* waitForCompletion(task.id);
    if (completionId) {
      yield put(
        snoozeTaskCompletion({
          employeeId,
          taskIds: [completionId],
          notes: null,
          until: moment().endOf('day').toISOString(),
        })
      );
      yield delay(500);
    }
    yield* refreshTasks();
  }
}

export function* watchTasksSagas() {
  yield takeEvery('ADD_TASK_SUCCESS', handleAddTaskSuccess);
  yield takeEvery('SAVE_TASK_SUCCESS', handleSaveTaskSuccess);
}
